import * as google_protobuf_empty_pb from 'google-protobuf/google/protobuf/empty_pb';

import conciergeBillingService from '@api/services/billingConciergeService';
import request from '@api/base/request';
import streamRequest from '@api/base/streamRequest';

import * as common_pb from 'respona_api/generated/common_pb';
import {
  BillingConciergeAddressType,
  BillingConciergePriceRangeType,
  BillingConciergeType,
  InvoiceConciergeDetailsType,
  InvoiceConciergeType,
} from '@ts/billing-concierge.types';
import { DateRangeNumberType, FileResponseType } from '@ts/common.types';
import BillingConciergeModels, {
  BillingConciergeCancellationRequest,
} from 'respona_api/generated/billing-concierge_pb';

// ================================================================================ BILLING
export const getBillingPriceRangesApi = (): Promise<BillingConciergePriceRangeType> => {
  const client = conciergeBillingService.getInstance();
  const emptyRequest = new google_protobuf_empty_pb.Empty();

  return request<BillingConciergePriceRangeType>(
    client,
    client.getBillingPriceRanges,
    emptyRequest
  );
};

export const getConciergeBillingDetailsApi = (): Promise<BillingConciergeType> => {
  const client = conciergeBillingService.getInstance();
  const emptyRequest = new google_protobuf_empty_pb.Empty();

  return request<BillingConciergeType>(client, client.getBillingDetails, emptyRequest);
};

export const cancelConciergeBillingSubscriptionApi = (
  feedback: string
): Promise<BillingConciergeType> => {
  const client = conciergeBillingService.getInstance();
  const cancellationRequest = new BillingConciergeCancellationRequest();
  cancellationRequest.setFeedback(feedback);

  return request<BillingConciergeType>(
    client,
    client.cancelBillingSubscription,
    cancellationRequest
  );
};

export const resumeConciergeBillingSubscriptionApi = (): Promise<void> => {
  const client = conciergeBillingService.getInstance();
  const emptyRequest = new google_protobuf_empty_pb.Empty();

  return request<void>(client, client.resumeBillingSubscription, emptyRequest);
};

export const calcDiscountApi = (amount: number): Promise<{ value: number }> => {
  const client = conciergeBillingService.getInstance();
  const discountRequest = new BillingConciergeModels.BillingConciergeDiscountRequest();

  discountRequest.setAmount(amount);

  return request<{ value: number }>(client, client.calcDiscount, discountRequest);
};

// ================================================================================ CREDIT_CARD

export const updateConciergeCreditCardInfoApi = (
  braintreeNonce: string,
  braintreeDeviceData: string
): Promise<BillingConciergeModels.CreditCardInfo.AsObject> => {
  const client = conciergeBillingService.getInstance();
  const updateRequest = new BillingConciergeModels.UpdateCreditCardRequest();

  updateRequest.setBraintreeNonce(braintreeNonce);
  updateRequest.setBraintreeDeviceData(braintreeDeviceData);

  return request<BillingConciergeModels.CreditCardInfo.AsObject>(
    client,
    client.updateCreditCardInfo,
    updateRequest
  );
};

// ================================================================================ INVOICES

export const getInvoicesApi = (
  page: number,
  limit: number,
  searchString?: string,
  dateRange?: DateRangeNumberType
): Promise<InvoiceConciergeType[]> => {
  const client = conciergeBillingService.getInstance();
  const invoiceRequest = new BillingConciergeModels.InvoiceRequest();

  invoiceRequest.setPage(page);
  invoiceRequest.setLimit(limit);

  if (searchString) {
    invoiceRequest.setQuery(searchString);
  }

  if (dateRange) {
    if (dateRange.startDate != null) {
      const fromFilter = new BillingConciergeModels.InvoiceFilter();
      fromFilter.setOrder(0);
      fromFilter.setField(BillingConciergeModels.InvoiceFilterField.DATE_INVOICE_ISSUE_DATE);
      fromFilter.setOperation(common_pb.FilterOperation.NUMBER_GREATER_THAN_OR_EQUAL);
      fromFilter.setValue(dateRange.startDate.toString());
      fromFilter.setJoin(common_pb.FilterJoinType.JOIN_QUERY_AND);

      invoiceRequest.addFilters(fromFilter);
    }

    if (dateRange.endDate != null) {
      const toFilter = new BillingConciergeModels.InvoiceFilter();
      toFilter.setOrder(1);
      toFilter.setField(BillingConciergeModels.InvoiceFilterField.DATE_INVOICE_ISSUE_DATE);
      toFilter.setOperation(common_pb.FilterOperation.NUMBER_LESS_THAN_OR_EQUAL);
      toFilter.setValue(dateRange.endDate.toString());
      toFilter.setJoin(common_pb.FilterJoinType.JOIN_QUERY_AND);

      invoiceRequest.addFilters(toFilter);
    }
  }

  return streamRequest<InvoiceConciergeType[]>(client, client.getInvoices, invoiceRequest);
};

export const getInvoiceDetailsApi = (
  workspaceId: number,
  invoiceId: number
): Promise<InvoiceConciergeDetailsType> => {
  const client = conciergeBillingService.getInstance();
  const idRequest = new common_pb.IdRequest();
  idRequest.setId(invoiceId);
  idRequest.setOptionalWorkspaceId(workspaceId);

  return request<InvoiceConciergeDetailsType>(client, client.getInvoiceDetails, idRequest);
};

// ================================================================================ PAYMENTS

export const getConciergeBraintreeTokenApi = (): Promise<{ value: string }> => {
  const client = conciergeBillingService.getInstance();
  const emptyRequest = new google_protobuf_empty_pb.Empty();

  return request<{ value: string }>(client, client.getBraintreeToken, emptyRequest);
};

export const makePaymentApi = (paymentPayload: {
  autoRenewal: boolean;
  amount: number;
  braintreeNonce: string;
  braintreeDeviceData: string;
  optionalRecipient?: {
    company: string;
    addressFirstLine: string;
    addressSecondLine: string;
    city: string;
    zipCode: string;
    state: string;
    country: string;
    fullName: string;
    tax: string;
  };
}): Promise<BillingConciergeModels.PaymentResponse.AsObject> => {
  const client = conciergeBillingService.getInstance();
  const paymentRequest = new BillingConciergeModels.PaymentRequest();

  paymentRequest.setAutoRenewal(paymentPayload.autoRenewal);
  paymentRequest.setAmount(paymentPayload.amount);
  paymentRequest.setBraintreeNonce(paymentPayload.braintreeNonce);
  paymentRequest.setBraintreeDeviceData(paymentPayload.braintreeDeviceData);

  if (paymentPayload.optionalRecipient) {
    const recipient = new BillingConciergeModels.InvoiceRecipient();
    recipient.setCompany(paymentPayload.optionalRecipient.company);
    recipient.setAddressFirstLine(paymentPayload.optionalRecipient.addressFirstLine);
    recipient.setAddressSecondLine(paymentPayload.optionalRecipient.addressSecondLine);
    recipient.setCity(paymentPayload.optionalRecipient.city);
    recipient.setZipCode(paymentPayload.optionalRecipient.zipCode);
    recipient.setState(paymentPayload.optionalRecipient.state);
    recipient.setCountry(paymentPayload.optionalRecipient.country);
    recipient.setFullName(paymentPayload.optionalRecipient.fullName);
    recipient.setTax(paymentPayload.optionalRecipient.tax);

    paymentRequest.setOptionalRecipient(recipient);
  }

  return request<BillingConciergeModels.PaymentResponse.AsObject>(
    client,
    client.makePayment,
    paymentRequest
  );
};

// ================================================================================ BILLING_ADDRESS

export const getBillingAddressApi = (): Promise<BillingConciergeAddressType> => {
  const client = conciergeBillingService.getInstance();
  const emptyRequest = new google_protobuf_empty_pb.Empty();

  return request<BillingConciergeAddressType>(client, client.getBillingAddress, emptyRequest);
};

export const updateBillingAddressApi = (
  addressFields: BillingConciergeAddressType
): Promise<BillingConciergeAddressType> => {
  const client = conciergeBillingService.getInstance();
  const billingAddress = new BillingConciergeModels.BillingAddress();

  if (addressFields.addressId) {
    billingAddress.setAddressId(addressFields.addressId);
  }
  billingAddress.setCompanyName(addressFields.companyName);
  billingAddress.setFirstLine(addressFields.firstLine);
  billingAddress.setSecondLine(addressFields.secondLine);
  billingAddress.setCity(addressFields.city);
  billingAddress.setZipCode(addressFields.zipCode);
  billingAddress.setState(addressFields.state);
  billingAddress.setCountry(addressFields.country);
  billingAddress.setContactEmail(addressFields.contactEmail);
  billingAddress.setContactName(addressFields.contactName);
  billingAddress.setTax(addressFields.tax);

  return request<BillingConciergeAddressType>(client, client.updateBillingAddress, billingAddress);
};

export const generateInvoicePdfApi = (invoiceId: number): Promise<FileResponseType> => {
  const client = conciergeBillingService.getInstance();
  const idRequest = new common_pb.IdRequest();

  idRequest.setId(invoiceId);

  return request<FileResponseType>(client, client.generateInvoicePdf, idRequest);
};

export const generateInvoicesPdfArchiveApi = (
  invoiceIds: number[]
): Promise<FileResponseType> => {
  const client = conciergeBillingService.getInstance();
  const idContainer = new common_pb.IdContainer();

  idContainer.setIdList(invoiceIds);

  return request<FileResponseType>(client, client.generateInvoicesPdfArchive, idContainer);
};
